import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import AppErrorBoundary from './components/AppErrorBoundary';
import FirestoreRecoveryBar from './components/FirestoreRecoveryBar';
import OptionalFeatureBoundary from './components/OptionalFeatureBoundary';
import { initSentry, reportUploadFailureToSentry } from './lib/sentry';
import { installFirestoreClosedRecovery } from './lib/firestoreRecovery';
import { registerAppShellWorker } from './lib/appShell';
import { markBoot } from './lib/bootTiming';
import { isCompSharePath } from './lib/sharePath';
import './index.css';

// FIRST OPEN, piece 1 (packet J12): the first of six boot points, taken
// here because this is the earliest line of our own code that runs. Every
// other number on /hub-check's "First open" readout is measured against
// the page's time origin too, so this one says how long the browser spent
// fetching and parsing the bundle before any of our code got a turn.
// Deliberately ABOVE initSentry(): Sentry's init is itself part of that
// answer, and burying it under the mark would hide it.
markBoot('ldg:module-start');

// N14: the comp share page's address (/s/<token>) is a family's secret.
// Netlify serves it from its own entry (share.html, shareMain.tsx), which
// starts none of what follows. If this app answers that address anyway (the
// dev server, a missing rewrite), the error reporter, the upload reporter,
// the database recovery and the app-shell worker still stay off, so nothing
// carries the address anywhere. sentry.ts refuses on its own too.
const compSharePage = isCompSharePath(window.location.pathname);

// Init Sentry before anything else renders so even render-time crashes get
// reported. A missing DSN is a no-op (the /hub-check page surfaces this).
if (!compSharePage) initSentry();

// Every failed upload reports to Sentry (a parent's failed video, 2026-10-22,
// left no trace: upload errors are caught and shown on screen, never thrown).
// The manager imports nothing, so the wiring lives here.
//
// THROUGH A DYNAMIC IMPORT (speed plan P2 lever 5): a static import here put
// the whole upload manager back into the startup bundle, which
// tests/startup-eager-files.test.mjs catches. Nothing is lost to the wait:
// the manager holds a failure reported before this lands and hands it over,
// and lib/sentry.ts queues it while the SDK is still loading. The app-wide
// StillSendingBanner loads the same chunk on every page anyway, so this
// fetches nothing extra. A failed load (a deploy replaced the chunk, no
// signal) costs only this visit's upload reports and must never reach the
// stale-chunk reload in lib/lazyRoute.ts.
if (!compSharePage) {
  void import('./lib/attachmentUploadManager')
    .then((manager) => manager.setManagedUploadFailureReporter(reportUploadFailureToSentry))
    .catch(() => undefined);
}

// Packet B815: when an iPhone closes the Hub's database under a page, every
// Firestore call fails until a reload (Sentry JAVASCRIPT-REACT-T and the b815
// family). This listens for exactly that and reloads once, safely, or shows
// "The Hub lost its connection. Tap to reload." The rules and the reasons are
// in lib/firestoreClosedRecovery.ts. After initSentry() so its report has a
// client to go to; before the render below, so nothing Firestore does can
// happen before it is listening. It cannot throw.
if (!compSharePage) installFirestoreClosedRecovery();

// THE WHITE SCREEN, piece 2: register the app-shell caching worker for
// every visitor (see src/lib/appShell.ts for why this cannot just live
// behind push's own opt-in registration call). Fire-and-forget - never
// blocks or affects the render below.
if (!compSharePage) registerAppShellWorker();

// Stale-chunk recovery (a deploy replaces hashed chunk files while a tab is
// still open, so its next dynamic import asks for a file that no longer
// exists) used to be handled HERE, listening for Vite's vite:preloadError
// and calling event.preventDefault() before reloading. REMOVED 2026-08-13:
// that listener was itself the reason a stale chunk could still crash into
// AppErrorBoundary despite "fixing" this once already (2026-07-28/29).
//
// Two compounding problems, both proven by reading the actual shipped Vite
// and React source plus a real `vite build` output, then confirmed by
// executing the exact sequence against this app's real reloadGuard.ts:
//   1. Calling event.preventDefault() on a FAILED dynamic import (as opposed
//      to a merely-preloaded dependency) does not make Vite retry or fail
//      cleanly - it makes Vite's internal handler swallow the throw and let
//      the import "resolve" with `undefined` instead. React's production
//      lazy resolution then runs `_result.default` on that `undefined` with
//      no defensive check, which is exactly Sentry's "Cannot read properties
//      of undefined (reading 'default')" / "e._result.default" crash - a
//      new failure THIS listener's own preventDefault() caused, landing
//      moments after its own "stale-chunk reload" breadcrumb.
//   2. Even a fixed version of this listener would still race lib/lazyRoute.ts's
//      per-import recovery: a window-level listener fires synchronously
//      INSIDE the promise chain lazyRoute.ts is awaiting, so it always
//      reaches reloadGuard.ts's one-shot cooldown first and leaves
//      lazyRoute.ts's own attempt permanently blocked, rethrowing on the
//      very first failure it ever sees.
//
// The fix is consolidation, not another listener: every route-shaped lazy
// import in this app goes through lazyRoute.ts's lazyRoute()/loadRouteModule(),
// whose own catch block is now the ONE place that calls
// reloadGuard.ts's triggerStaleChunkReload() (same cooldown, same Sentry
// breadcrumb, same reload - moved there unchanged, not duplicated). With
// nothing left to call preventDefault(), Vite always rejects a stale chunk
// cleanly, and lazyRoute.ts's wrapper is always the first and only responder.
// Full diagnosis, including the probe output that proved both halves, is in
// lib/lazyRoute.ts's doc comment and this change's PR description.

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </AppErrorBoundary>
    {/* Beside the app, not inside it: the dead database can take the app
        down to AppErrorBoundary's screen, and the bar still has to show.
        Its own boundary means a fault in it can never take the app down. */}
    <OptionalFeatureBoundary feature="FirestoreRecoveryBar">
      <FirestoreRecoveryBar />
    </OptionalFeatureBoundary>
  </React.StrictMode>
);

// Boot watchdog handshake (task #125): index.html arms a 15s inline timer
// that replaces a still-empty #root with a plain-HTML reload offer, and
// window.__ldgBooted disarms it.
//
// THAT FLAG IS NO LONGER SET HERE (first opening audit, I-3). It used to be
// the statement right after the render call above, which meant "the bundle
// parsed and render was kicked off" - and React 18's createRoot().render()
// SCHEDULES work, it does not commit it. Proved in a browser against the
// production build, with a setter planted on window.__ldgBooted: at the
// moment the flag was written, #root still held the static boot shell. So
// the one escape that does not depend on React having mounted anything was
// being disarmed by a bundle that had merely started.
//
// It is set from the first COMMITTED render of the shell instead, in a
// layout effect at the top of src/App.tsx. A render that throws never
// reaches it, which is the one case the reload offer exists for.
